import {Component, HostListener, Inject, LOCALE_ID, OnInit, PLATFORM_ID} from '@angular/core';
import {ActivatedRoute, NavigationEnd, NavigationStart, Router} from '@angular/router';
import {filter, map, take} from 'rxjs/operators';
import {AuthService} from './services/auth.service';
import {AnalyticsService} from './services/analytics.service';
import {MetaService} from '@src/@ngx-meta/core/src';
import {TranslationService} from './services/translation.service';
import {DOCUMENT, isPlatformBrowser} from '@angular/common';
import {VersionService} from './services/version.service';
import {KeyboardService} from './services/keyboard.service';
import {SentryService} from './services/sentry.service';
import dayjs from 'dayjs';
import {Dayjs} from 'dayjs';
import {DateAdapter} from '@angular/material/core';
import {Platform} from '@angular/cdk/platform';
import {SafariScreenFixService} from './services/safari-screen-fix.service';
import {UserService} from './services/userService/user.service';
import {LoggingService} from '@app/services/util/logging.service';
import {ConfigAPIService} from '@app/services/api/config-api.service';
import {BehaviorSubject, Observable, repeat, zip} from 'rxjs';
import {BusinessUnitsService} from './services/accounts/business-units.service';
import {isMobile} from '@app/util/browser.helper';
import {environment} from '@src/environments/environment';
import {SubscriptionExpiredDialogComponent} from '@app/dialogs/subscription-expired-dialog/subscription-expired-dialog.component';
import {AccountLockedDialogComponentComponent} from '@app/dialogs/account-locked-dialog-component/account-locked-dialog-component.component';
import {OpenProductPackageOfferDialogComponentComponent} from '@app/dialogs/open-product-package-offer-dialog-component/open-product-package-offer-dialog-component.component';
import {ExtensionStatusDialogComponent} from '@app/dialogs/extension-status-dialog/extension-status-dialog.component';
import {ExtensionStatusService} from '@app/services/extension-status.service';
import {isQuickEntryDialogSuppressed} from '@app/util/quick-entry-dismissal.helper';
import {AccountFeaturesEnum} from '@app/interfaces/config/accountFeaturesEnum';
import {PermissionEnum} from '@app/enums/permissions/PermissionEnum';
import {AccountConfig} from '@app/interfaces/config/account.config';
import {MatDialog} from '@angular/material/dialog';
import {productFruits} from 'product-fruits';
import {LocaleRedirectService} from './services/util/locale-redirect.service';
import {UserProfile} from '@app/interfaces/entities/user-profile';
import {isBrowser} from './util/is-browser';
import {BadgeService} from './services/badge.service';
import {NotificationApiService} from './services/api/notification-api.service';
import {FirebaseService} from "@app/services/firebase.service";

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit {

  public control$: Observable<{ name: string; accountUuid: string } | null>;
  public appReady$ = new BehaviorSubject<boolean>(false);

  public appHasError = false;

  constructor(private router: Router,
              private activatedRoute: ActivatedRoute,
              private metaService: MetaService,
              private authService: AuthService,
              private translation: TranslationService,
              private analyticsService: AnalyticsService,
              private versionService: VersionService,
              private keyboardService: KeyboardService,
              private sentry: SentryService,
              private dateAdapter: DateAdapter<Dayjs>,
              private platform: Platform,
              private userService: UserService,
              private safariScreenFix: SafariScreenFixService,
              private loggingService: LoggingService,
              private configService: ConfigAPIService,
              private businessUnitsService: BusinessUnitsService,
              private matDialog: MatDialog,
              private localeRedirectService: LocaleRedirectService,
              private badgeService: BadgeService,
              private firebaseService: FirebaseService,
              private extensionStatusService: ExtensionStatusService,
              @Inject(PLATFORM_ID) private platformId: any,
              @Inject(DOCUMENT) private document,
              @Inject(LOCALE_ID) public locale: string,
  ) {

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd || event instanceof NavigationStart))
      .subscribe(this.loggingService.debug);

    dayjs.locale(locale);

    this.dateAdapter.setLocale(locale);
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event) {
    this.keyboardService.emit(event);
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange() {
    // Clear the badge when user opens/focuses on the app
    if (isPlatformBrowser(this.platformId) && document.visibilityState === 'visible') {
      this.badgeService.clearBadge();
    }
  }

  ngOnInit(): void {
    // Load initial config
    this.configService.getInitialConfig(this.locale).subscribe({
      error: (error) => {
        this.appHasError = true;
        console.error(error);
      }
    });

    // Block analytics if account is taken over — seed synchronously before tracking scripts load
    this.analyticsService.setIsAccountControl(!!this.authService.getAccountControl()?.accountUuid);
    this.analyticsService.initClientSideTracking();

    this.authService.getAccountControlChanges().subscribe((control) => {
      this.analyticsService.setIsAccountControl(!!control?.accountUuid);
    });

    // this.completed();
    this.authService.getAuthChanges().subscribe((auth) => {
      this.analyticsService.setUser(auth);

      if (auth) {
        this.analyticsService.fireBriefdAnalyticsEvent('open').subscribe();
        this.sentry.setUser(auth);
        this.authService.setupCookieForEasierImageLoading().subscribe();
      } else {
        this.sentry.setUser(null);
      }
    });

    this.control$ = this.authService.getAccountControlChanges();
    this.control$.subscribe((control) => {
      if (control) {
        this.businessUnitsService.afterAuthChanged();
      }
    });

    // Setup Firebase Cloud Messaging for push notifications
    // Only subscribe when user is authenticated to avoid 401 on public pages (e.g. password reset)
    if (this.authService.isLoggedIn()) {
      this.firebaseService.generateToken().then((token) => {
        if (token) {
          this.firebaseService.subscribe(token).subscribe({
            next: (result) => {
              console.log('[FCM] Successfully subscribed to push notifications');
              this.firebaseService.setupForegroundMessageListener();
            },
            error: (error) => {
              console.error('[FCM] Failed to subscribe to push notifications:', error);
            }
          });
        }
      }).catch((error) => {
        console.error('[FCM] Token generation error:', error);
      });
    }

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map((event) => {

        const title = this.extractTitle(this.activatedRoute);

        //  As the tiles are title.id, just remove the title.
        if (title) {
          this.metaService.setTitle(this.translation.getTranslation(title.slice(6, title.length)));
        }

        // @ts-ignore
        this.analyticsService.firePageEvent(event.urlAfterRedirects, title);

        return title;
      })
    ).subscribe();

    if (this.authService.isLoggedIn()) {
      this.configService.getAccountConfig().pipe(take(1))
        .subscribe((config) => {
          this.analyticsService.setAccountConfig(config);
          this.maybeShowExtensionStatusDialog(config);
        });

      zip(
        this.configService.getProductFruitsData(),
        this.userService.getUserProfile()).subscribe(([productFruitsData, user]) => {
        if (user) {
          if (!environment.product_fruits.code.startsWith('x')) {
            (window as any).productFruits = (window as any).productFruits || productFruits;
            (window as any).productFruits.init(environment.product_fruits.code, user.originalLocale, productFruitsData,
              {
                hideInAppCenterLauncher: true
              });
          }

          this.initCrisp(user);
          this.businessUnitsService.afterAuthChanged();

          if (environment.production) {
            this.localeRedirectService.compareUserLocaleVsSiteLocaleAndRedirect(user);
          }

        }

        this.appReady$.next(true);
      });

      // Check if Package subscription is expired
      this.configService.getAccountConfig().pipe(
        repeat({delay: 3600 * 1000})
      ).subscribe({
        next: (config) => {
          if (config.isDeactivated) {
            // Must subscribe — logout() is a cold Observable, so a bare call revoked nothing and
            // left the session in localStorage, letting a deactivated user walk back into /admin.
            this.authService.logout().subscribe();
            this.router.navigate(['/deactivated']);
          } else if (config.isLocked) {
            AccountLockedDialogComponentComponent.open(this.matDialog, config);
          } else if (config.activeAccountProductPackage?.isExpired === undefined || config.activeAccountProductPackage.isExpired) {
            SubscriptionExpiredDialogComponent.open(this.matDialog, config);
          } else if (!config.activeAccountProductPackage?.isTrial && (!config.activeAccountProductPackage?.isSigned && !config.activeAccountProductPackage?.isRejected && config.activeAccountProductPackage.paymentUrl)) {
            OpenProductPackageOfferDialogComponentComponent.open(this.matDialog, config);
          }
        },
        error: (err) => {
          // Don't retry on 503 or 5xx errors - just log and stop
          console.error('Failed to load account config:', err);
        }
      });
    } else {
      // If user is not logged in, mark app as ready immediately
      this.appReady$.next(true);
    }
  }

  private maybeShowExtensionStatusDialog(config: AccountConfig): void {
    if (isMobile()) {
      return;
    }

    if (!config.accountFeatureFlags?.[AccountFeaturesEnum.IS_AVAILABLE_EXTENSION_VEHICLE_IMPORT]) {
      return;
    }

    this.userService.getUserProfile().pipe(take(1)).subscribe((user) => {
      if (!user) {
        return;
      }

      const hasPermission = user.isSuperAdmin || user.permissions?.[PermissionEnum.TAB_VEHICLES_ADS_VIEW] === true;
      if (!hasPermission) {
        return;
      }

      if (!user.userUuid) {
        return;
      }

      if (isQuickEntryDialogSuppressed(user.userUuid)) {
        return;
      }

      this.extensionStatusService.checkStatus().then((status) => {
        if (status === 'installed-logged-in') {
          return;
        }

        ExtensionStatusDialogComponent.open(this.matDialog, {status, userId: user.userUuid});
      });
    });
  }

  private initCrisp(user: UserProfile): void {
    if (!isBrowser()) return;
    if (!environment.crisp_website_id || environment.crisp_website_id === 'XXX') return;
    if ((window as any).CRISP_WEBSITE_ID) return;

    (window as any).$crisp = [];
    (window as any).CRISP_WEBSITE_ID = environment.crisp_website_id;

    const script = this.document.createElement('script');
    script.src = 'https://client.crisp.chat/l.js';
    script.async = true;
    script.onload = () => {
      (window as any).$crisp.push(['safe', true]);
      (window as any).$crisp.push(['do', 'chat:hide']);
      (window as any).$crisp.push(['config', 'position:reverse', [false]]);

      this.maybeResetCrispSession(user);

      const fullName = [user.profile?.firstName, user.profile?.lastName].filter(Boolean).join(' ');
      if (fullName) {
        (window as any).$crisp.push(['set', 'user:nickname', [fullName]]);
      }
      if (user.email) {
        (window as any).$crisp.push(['set', 'user:email', [user.email]]);
      }

      const sessionData = Object.entries({
        user_uuid: user.uuid,
        account_uuid: user.publicAccount?.accountUuid,
        account_name: user.publicAccount?.name,
      }).filter((entry): entry is [string, string] => !!entry[1]);

      if (sessionData.length > 0) {
        (window as any).$crisp.push(['set', 'session:data', [sessionData]]);
      }

      (window as any).$crisp.push(['on', 'chat:closed', () => {
        (window as any).productFruits?.api?.inAppCenter?.open?.();
      }]);
    };
    script.onerror = () => {
      delete (window as any).$crisp;
      delete (window as any).CRISP_WEBSITE_ID;
    };

    this.document.head.appendChild(script);
  }

  /**
   * Crisp keeps one persistent session per browser, so a returning client's messages keep landing
   * in the same conversation — and the Slack integration mirrors that into the same old Slack
   * thread instead of a new top-level message. Resetting the session every 30 days forces Crisp to
   * start a fresh conversation (new Slack thread) instead of appending to a stale one indefinitely.
   */
  private maybeResetCrispSession(user: UserProfile): void {
    const accountUuid = user.publicAccount?.accountUuid;
    if (!accountUuid) return;

    const storageKey = `crisp_session_reset_${accountUuid}`;
    const lastReset = Number(localStorage.getItem(storageKey));
    const daysSinceReset = lastReset ? dayjs().diff(dayjs(lastReset), 'day') : null;

    if (daysSinceReset === null) {
      localStorage.setItem(storageKey, String(Date.now()));
      return;
    }

    if (daysSinceReset >= 30) {
      (window as any).$crisp.push(['do', 'session:reset']);
      localStorage.setItem(storageKey, String(Date.now()));
    }
  }

  private convertBooleanObjectToArray(obj: { [key: string]: boolean }): string[] {
    return Object.keys(obj).filter(key => obj[key] === true);
  }

  private extractTitle(activatedRoute: ActivatedRoute): any {
    let title: any = null;
    let child = activatedRoute.firstChild;

    if (child.snapshot.data.title) {
      title = child.snapshot.data.title;
    }

    //  We might have a more complex route, try to show the title of the "last" link in the path
    while (child.children.length > 0) {
      child = child.firstChild;
      if (child.snapshot.data.title) {
        title = child.snapshot.data.title;
      }
    }

    return title;
  }

  releaseControl() {
    this.authService.takeControl(null);

    if (isBrowser()) {
      window.location.reload();
    }
  }

  @HostListener('window:resize', ['$event'])
  @HostListener('window:load', ['$event'])
  getScreenSize(event?) {
    this.safariScreenFix.setIsSafari(this.platform.SAFARI || this.platform.IOS);
    if (!this.safariScreenFix.getIsSafari() && isBrowser()) {
      const vh = window.innerHeight * 0.01;
      this.document.documentElement.style.setProperty('--vh', `${vh}px`);
    }
  }

}
